// src/views/PortalQuoteDetail.js
// PHASE 4 VERIFIER: r1-p4-portal-9c4d8b21
//
// Customer-facing quote approval page. Phase 4 of the selective-
// approval feature rewrite. Replaces the prior thin direct-write
// approve/decline UI with:
//   - Per-line-item selection (Required / Recommended / Add-on / Included)
//   - Live recalc of subtotal, tax, total
//   - Signature capture (typed or drawn)
//   - Three distinct actions: Decline / Request Changes / Approve Selected
//   - Decline confirmation modal
//   - Request Changes modal with a note input
//   - Post-approval / post-decline / post-changes-requested read-only modes
//   - Company logo header (was missing — quotes never had it)
//   - Defensive fallback to legacy approve-all mode for pre-migration data
//
// All approval / decline / changes-requested writes now go through
// Cloud Functions (approveQuoteV2, requestQuoteChanges) instead of
// direct client-side writes. The CFs run server-side validation,
// compute approved totals, write the immutable audit trail to
// /approvals/, and let the existing onQuoteStatusChanged trigger
// handle notifications and linked-job scheduling.

// SEAM 7 VERIFIER: r1-dep-portalui-6d2b8f39
// Deposits Roadmap — Seam 7 adds the customer-facing deposit flow to this page:
// the hybrid approve/pay presentation, AwaitingDeposit recognition (previously
// this page matched that status nowhere and rendered a dead read-only view), and
// the post-Checkout confirmation that watches the quote doc until the webhook lands.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import LineDescription from '../components/documents/LineDescription'; // linetext-4d8b2e61 — line descriptions keep line breaks, indents and bullets
import { LINE_NAME, LINE_DESC } from '../components/documents/lineItemStyles'; // linetext-4d8b2e61 — the PDF's line layout
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc } from 'firebase/firestore';
import { fetchPortalQuote } from '../utils/portalQuoteReads'; // x7portal-6e1a3d58 — estimates come from getPortalQuotes
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase/config';
import usePortalViewReport from '../hooks/usePortalViewReport'; // qview-6b2d4e91
import { isStaffPreviewBrowser } from '../utils/portalPreview'; // s8audit-7c2e5b94 — S8b P2
import { downloadPdfBytes } from '../utils/pdfDownload'; // docpdf-8a4c1e73 — PDFs as bytes; nothing stored, no link
import { customerTaxRows, taxRowText } from '../utils/lineTax'; // taxlines2-3e0b16a4 — one row per tax part when the stored parts add up; taxexempt3-8c4f2a1d — taxRowText
import {
    Loader2, ArrowLeft, Check, X, Calendar, Lock, Star, Plus, Link2,
    AlertCircle, FileSignature, Edit3, Type, Clock, MessageSquare, Download,
    CreditCard, // r1-dep-portalui-6d2b8f39
    ScrollText, // doced-8f3a61c2
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import {
    SELECTION_TYPES,
    isApprovedStatus,
    isDeclinedStatus,
    isChangesRequestedStatus,
    resolveDocTerminology, // r1-p3-docterm-pqdetail-c4f8a1e9
    // r1-dep-portalui-6d2b8f39 — deposit constants + the client mirrors of the
    // server's money math and required-flag resolution.
    QUOTE_STATUSES,
    DEPOSIT_STATUSES,
    isValidDepositType,
    resolveDepositAmount,
    resolveDepositRequired,
    // qexp-6f2a9d38 — Seam 5: the ONE derived-expiry rule, shared with the
    // operator screens, the server guard and the warning cron.
    EXPIRY_STATES,
    resolveExpiryState,
    toExpiryDate,
    deletedQuotePortalMessage, // estdel-f9ac8ba8
} from '../utils/quoteSchema';
// SEAM 1b VERIFIER: pqd-orgpay-import-b8c9d0e1
// o3-portalproj-9c4e7b21 — the isOrgStripeReady import is GONE from this file: the
// page no longer reads the org doc, so it can't mirror the gate client-side. The
// same pre-flight check for the deposit Pay button is now computed server-side and
// arrives as the `stripeReady` boolean on the getPublicOrganizationInfo projection.
// src/utils/orgPayments.js itself stays — DepositSettings.js and QuoteFormModal.js
// (both STAFF screens, which still read the org doc) continue to use it.


// ════════════════════════════════════════════════════════════════════
//   SignaturePad — inline HTML5 canvas, no external dep
// ════════════════════════════════════════════════════════════════════
//
// Captures mouse + touch input, draws strokes, exports as SVG path
// data so the result is a string (not binary) suitable for Firestore.
// Strokes stored as an array of point arrays so we can rebuild the
// SVG path cleanly without re-tracing pixel data.
//
// Exposes via ref:
//   - clear()       — wipe the canvas and reset strokes
//   - isEmpty()     — true if no strokes drawn yet
//   - toSvgPath()   — serialized SVG path "M x1 y1 L x2 y2 L ... M ..."
const SignaturePad = React.forwardRef(({ disabled, onStrokeEnd }, ref) => {
    const canvasRef = useRef(null);
    const strokesRef = useRef([]);          // [[{x,y},{x,y},...], [...]]
    const currentStrokeRef = useRef(null);
    const isDrawingRef = useRef(false);

    // Set up canvas context (high-DPI aware) on mount and resize.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            const ctx = canvas.getContext('2d');
            ctx.scale(dpr, dpr);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#111827';
            redraw();
        };
        resize();
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, []);

    const redraw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#111827';
        for (const stroke of strokesRef.current) {
            if (stroke.length < 1) continue;
            ctx.beginPath();
            ctx.moveTo(stroke[0].x, stroke[0].y);
            for (let i = 1; i < stroke.length; i++) {
                ctx.lineTo(stroke[i].x, stroke[i].y);
            }
            ctx.stroke();
        }
    };

    const getPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const touch = e.touches?.[0];
        const clientX = touch ? touch.clientX : e.clientX;
        const clientY = touch ? touch.clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const start = (e) => {
        if (disabled) return;
        e.preventDefault();
        isDrawingRef.current = true;
        const pt = getPoint(e);
        currentStrokeRef.current = [pt];
        strokesRef.current.push(currentStrokeRef.current);
    };

    const move = (e) => {
        if (!isDrawingRef.current || disabled) return;
        e.preventDefault();
        const pt = getPoint(e);
        currentStrokeRef.current.push(pt);
        redraw();
    };

    const end = (e) => {
        if (!isDrawingRef.current) return;
        if (e) e.preventDefault();
        isDrawingRef.current = false;
        currentStrokeRef.current = null;
        // r1-p7fix-drawnsig-4b9e2c81 — notify parent a stroke completed so it can
        // re-evaluate has-drawn. Replaces the dead pointerEvents:none overlay.
        if (typeof onStrokeEnd === 'function') onStrokeEnd();
    };

    React.useImperativeHandle(ref, () => ({
        clear: () => {
            strokesRef.current = [];
            currentStrokeRef.current = null;
            redraw();
        },
        isEmpty: () => strokesRef.current.length === 0
            || strokesRef.current.every(s => s.length < 2),
        toSvgPath: () => {
            return strokesRef.current
                .filter(s => s.length >= 1)
                .map(s => {
                    const head = `M ${s[0].x.toFixed(2)} ${s[0].y.toFixed(2)}`;
                    const tail = s.slice(1).map(p => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
                    return tail ? `${head} ${tail}` : head;
                })
                .join(' ');
        },
    }));

    return (
        <canvas
            ref={canvasRef}
            className="w-full h-40 bg-white border-2 border-dashed border-gray-300 rounded-md touch-none cursor-crosshair"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
        />
    );
});


// ════════════════════════════════════════════════════════════════════
//   SignatureModal — collects signature + submits approval
// ════════════════════════════════════════════════════════════════════
// r1-dep-portalui-6d2b8f39 — depositAmount/depositRequired added so a gated approval
// says plainly, BEFORE the signature, that payment is what completes it and that the
// next tap leaves for a payment page.
const SignatureModal = ({ open, onClose, onSubmit, approvedTotal, currencyCode, processing, error, docWord = 'Quote', depositAmount = 0, depositRequired = false, stripeReady = true, companyName = '' }) => { // r1-p3-docterm-pqdetail-c4f8a1e9 / SEAM 1d pqd-sigmodal-props-b5c1e7a9
    const [mode, setMode] = useState('typed');           // 'typed' | 'drawn'
    const [typedName, setTypedName] = useState('');
    const [hasDrawn, setHasDrawn] = useState(false);     // re-render when drawn status changes
    const padRef = useRef(null);

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setMode('typed');
            setTypedName('');
            setHasDrawn(false);
        }
    }, [open]);

    if (!open) return null;

    const handleSubmit = () => {
        if (mode === 'typed') {
            const trimmed = typedName.trim();
            if (!trimmed) return;
            onSubmit({ type: 'typed', value: trimmed });
        } else {
            if (!padRef.current || padRef.current.isEmpty()) return;
            const svg = padRef.current.toSvgPath();
            onSubmit({ type: 'drawn', value: svg });
        }
    };

    const canSubmit = mode === 'typed' ? typedName.trim().length > 0 : hasDrawn;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <FileSignature className="text-green-600" size={20} />
                        <h2 className="text-lg font-bold text-gray-900">Approve {docWord}</h2>{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                    </div>
                    <button onClick={onClose} disabled={processing} className="text-gray-400 hover:text-gray-600 disabled:opacity-40">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-gray-700">You're approving</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(approvedTotal, currencyCode)}</p>
                    </div>

                    {/* r1-dep-portalui-6d2b8f39 — deposit disclosure before signing. */}
                    {depositAmount > 0 && (
                        <div className={`border rounded-lg p-3 ${depositRequired ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex items-start gap-2.5">
                                <CreditCard size={16} className={`flex-shrink-0 mt-0.5 ${depositRequired ? 'text-amber-600' : 'text-gray-500'}`} />
                                <div>
                                    <p className={`text-sm font-semibold ${depositRequired ? 'text-amber-900' : 'text-gray-800'}`}>
                                        Deposit: {formatCurrency(depositAmount, currencyCode)}
                                    </p>
                                    <p className={`text-xs mt-1 ${depositRequired ? 'text-amber-800' : 'text-gray-600'}`}>
                                        {/* SEAM 1d VERIFIER: pqd-sigmodal-note-6a4f2b8d
                                            Only promise a secure payment page when the org can
                                            actually take one. A non-Stripe org arranges the
                                            deposit directly — don't imply an online payment. */}
                                        {depositRequired
                                            ? (stripeReady
                                                ? `Your approval completes once this deposit is paid. After you sign, we’ll take you straight to a secure payment page.`
                                                : `Your approval is saved as soon as you sign. ${companyName || 'The business'} will arrange this deposit with you directly.`)
                                            : `We’ll offer this after you approve — it’s not required to approve this ${docWord.toLowerCase()}.`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Sign to confirm:</p>
                        <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50 mb-3">
                            <button
                                onClick={() => setMode('typed')}
                                disabled={processing}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                    mode === 'typed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Type size={14} /> Type
                            </button>
                            <button
                                onClick={() => setMode('drawn')}
                                disabled={processing}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                    mode === 'drawn' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Edit3 size={14} /> Draw
                            </button>
                        </div>

                        {mode === 'typed' && (
                            <input
                                type="text"
                                value={typedName}
                                onChange={(e) => setTypedName(e.target.value)}
                                placeholder="Type your full name"
                                disabled={processing}
                                autoFocus
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base font-medium focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none disabled:bg-gray-100"
                                style={{ fontFamily: '"Brush Script MT", "Comic Sans MS", cursive' }}
                            />
                        )}

                        {mode === 'drawn' && (
                            <div>
                                {/* r1-p7fix-drawnsig-4b9e2c81 — onStrokeEnd flips hasDrawn after each
                                    completed stroke (replaces the dead pointerEvents:none overlay,
                                    which received no events and left the submit button stuck disabled). */}
                                <SignaturePad
                                    ref={padRef}
                                    disabled={processing}
                                    onStrokeEnd={() => setHasDrawn(!padRef.current?.isEmpty())}
                                />
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">Sign with your finger or mouse</p>
                                    <button
                                        type="button"
                                        onClick={() => { padRef.current?.clear(); setHasDrawn(false); }}
                                        disabled={processing}
                                        className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-40"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <p className="text-xs text-gray-500">
                        By signing, you authorize the work described in this {docWord.toLowerCase()} at the amount shown.{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                        An audit record (including time and date) will be kept.
                    </p>

                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 p-5 border-t border-gray-100 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={processing}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-sm disabled:opacity-40"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={processing || !canSubmit}
                        className="px-5 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-bold text-sm shadow-sm flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {processing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {/* SEAM 1d VERIFIER: pqd-sigbtn-label-a3f19c72
                            Only promise payment when the org can actually take one. With
                            Stripe, a gated approval redirects to Checkout, so the button
                            says where it's going ('Taking you to payment…' also covers the
                            beat between the CF returning and the browser navigating). Without
                            Stripe there's no payment page, so the button just says it's
                            approving — the customer arranges the deposit with the business. */}
                        {processing
                            ? (stripeReady && depositRequired && depositAmount > 0 ? 'Taking you to payment…' : 'Submitting…')
                            : (stripeReady && depositRequired && depositAmount > 0 ? 'Sign & Continue to Payment' : 'Approve & Sign')}
                    </button>
                </div>
            </div>
        </div>
    );
};


// ════════════════════════════════════════════════════════════════════
//   DeclineConfirmModal — confirmation before declining
// ════════════════════════════════════════════════════════════════════
const DeclineConfirmModal = ({ open, onClose, onConfirm, processing, error, docWord = 'Quote' }) => { // r1-p3-docterm-pqdetail-c4f8a1e9
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-900">Decline this {docWord.toLowerCase()}?</h2>{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                    <button onClick={onClose} disabled={processing} className="text-gray-400 hover:text-gray-600 disabled:opacity-40">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-sm text-gray-700">
                        Once you decline, you won't be able to approve this {docWord.toLowerCase()} later. If you'd like to discuss
                        changes instead, use the <strong>Request Changes</strong> button.
                    </p>
                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 p-5 border-t border-gray-100 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={processing}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-sm disabled:opacity-40"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={processing}
                        className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-bold text-sm shadow-sm flex items-center gap-2 disabled:bg-gray-300"
                    >
                        {processing ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                        {processing ? 'Declining…' : `Yes, Decline ${docWord}`}
                    </button>
                </div>
            </div>
        </div>
    );
};


// ════════════════════════════════════════════════════════════════════
//   RequestChangesModal — note input + submit
// ════════════════════════════════════════════════════════════════════
const RequestChangesModal = ({ open, onClose, onSubmit, processing, error }) => {
    const [note, setNote] = useState('');
    useEffect(() => { if (open) setNote(''); }, [open]);

    if (!open) return null;

    const handleSubmit = () => {
        const trimmed = note.trim();
        if (!trimmed) return;
        onSubmit(trimmed);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <MessageSquare className="text-blue-600" size={20} />
                        <h2 className="text-lg font-bold text-gray-900">Request changes</h2>
                    </div>
                    <button onClick={onClose} disabled={processing} className="text-gray-400 hover:text-gray-600 disabled:opacity-40">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-sm text-gray-700">
                        Let the business know what you'd like to change. They'll review your note and follow up.
                    </p>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g., Can we discuss the price of the interior detailing?"
                        rows={4}
                        maxLength={5000}
                        disabled={processing}
                        autoFocus
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none disabled:bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 text-right">{note.length} / 5000</p>
                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 p-5 border-t border-gray-100 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={processing}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-sm disabled:opacity-40"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={processing || note.trim().length === 0}
                        className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold text-sm shadow-sm flex items-center gap-2 disabled:bg-gray-300"
                    >
                        {processing ? <Loader2 size={16} className="animate-spin" /> : <MessageSquare size={16} />}
                        {processing ? 'Sending…' : 'Send Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};


// ════════════════════════════════════════════════════════════════════
//   Pure helper functions (extracted so they're testable / readable)
// ════════════════════════════════════════════════════════════════════

// Compute final state map and totals. Mirrors approveQuoteV2 server-side
// math exactly so the customer's live preview matches what'll actually
// be charged.
function computeApprovalState(lineItems, selectedSet) {
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
        return {
            finalStateByItemId: new Map(),
            approvedSubtotal: 0,
            approvedTaxableSubtotal: 0,
            originalSubtotal: 0,
            originalTaxableSubtotal: 0,
            approvedItemIds: [],
            declinedItemIds: [],
        };
    }

    const finalStateByItemId = new Map();

    // Pass 1: non-included lines
    for (const item of lineItems) {
        if (item.selectionType === SELECTION_TYPES.INCLUDED) continue;
        let state;
        switch (item.selectionType) {
            case SELECTION_TYPES.REQUIRED:
                state = 'auto-included-required';
                break;
            case SELECTION_TYPES.RECOMMENDED:
            case SELECTION_TYPES.ADDON:
                state = selectedSet.has(item.itemId) ? 'approved' : 'declined-by-customer';
                break;
            default:
                // Unknown / legacy items (no selectionType) treated as required
                state = 'auto-included-required';
        }
        finalStateByItemId.set(item.itemId, state);
    }

    // Pass 2: included lines inherit from parent
    for (const item of lineItems) {
        if (item.selectionType !== SELECTION_TYPES.INCLUDED) continue;
        const parentState = finalStateByItemId.get(item.parentItemId);
        if (!parentState) {
            finalStateByItemId.set(item.itemId, 'parent-declined');
            continue;
        }
        const parentApproved = parentState === 'approved' || parentState === 'auto-included-required';
        finalStateByItemId.set(item.itemId, parentApproved ? 'auto-included-required' : 'parent-declined');
    }

    // Compute totals
    let approvedSubtotal = 0;
    let approvedTaxableSubtotal = 0;
    let originalSubtotal = 0;
    let originalTaxableSubtotal = 0;
    const approvedItemIds = [];
    const declinedItemIds = [];

    for (const item of lineItems) {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        const lineTotal = qty * price;
        const taxable = !item.taxExempt;

        originalSubtotal += lineTotal;
        if (taxable) originalTaxableSubtotal += lineTotal;

        const state = finalStateByItemId.get(item.itemId);
        const isApproved = state === 'approved' || state === 'auto-included-required';
        if (isApproved) {
            approvedSubtotal += lineTotal;
            if (taxable) approvedTaxableSubtotal += lineTotal;
            if (item.itemId) approvedItemIds.push(item.itemId);
        } else {
            if (item.itemId) declinedItemIds.push(item.itemId);
        }
    }

    return {
        finalStateByItemId,
        approvedSubtotal,
        approvedTaxableSubtotal,
        originalSubtotal,
        originalTaxableSubtotal,
        approvedItemIds,
        declinedItemIds,
    };
}

// Proportional tax scaling — matches server-side approveQuoteV2.
function computeProportionalTax(originalTaxAmount, originalTaxable, approvedTaxable) {
    if (!Number.isFinite(originalTaxAmount) || originalTaxAmount === 0) return 0;
    if (!Number.isFinite(originalTaxable) || originalTaxable <= 0) return 0;
    const tax = originalTaxAmount * (approvedTaxable / originalTaxable);
    return Math.round(tax * 100) / 100;
}

// taxlines2-3e0b16a4 — Settings › Taxes companies: how long the page waits for the server's approval figures,
// and what it says when they don't come (Approve then stays disabled; the page never guesses the total).
export const PREVIEW_TIMEOUT_MS = 8000;
export const PREVIEW_FAILED_TEXT = "We couldn't update the total. Refresh the page to try again.";

// Check whether every line item has an itemId (post-migration safe).
function isLegacyQuote(lineItems) {
    if (!Array.isArray(lineItems) || lineItems.length === 0) return false;
    return lineItems.some(item => !item.itemId);
}

// Sort line items so each Included child renders right after its parent.
function orderItemsForDisplay(lineItems) {
    if (!Array.isArray(lineItems)) return [];
    // Separate parents (non-included) and included items.
    const nonIncluded = lineItems.filter(i => i.selectionType !== SELECTION_TYPES.INCLUDED);
    const included = lineItems.filter(i => i.selectionType === SELECTION_TYPES.INCLUDED);
    // For each parent (and any orphan), append children that point at it.
    const ordered = [];
    const seenIncluded = new Set();
    for (const item of nonIncluded) {
        ordered.push(item);
        for (const child of included) {
            if (child.parentItemId === item.itemId && !seenIncluded.has(child.itemId)) {
                ordered.push(child);
                seenIncluded.add(child.itemId);
            }
        }
    }
    // Any included items whose parent is missing — surface at the end so
    // the customer doesn't lose visibility of them entirely.
    for (const child of included) {
        if (!seenIncluded.has(child.itemId)) ordered.push(child);
    }
    return ordered;
}


// ════════════════════════════════════════════════════════════════════
//   LineItemRow — single row in the selection table
// ════════════════════════════════════════════════════════════════════
const LineItemRow = ({ item, isSelected, onToggle, readOnly, finalState, currencyCode }) => {
    const isIncluded = item.selectionType === SELECTION_TYPES.INCLUDED;
    const isRequired = item.selectionType === SELECTION_TYPES.REQUIRED;
    const isRecommended = item.selectionType === SELECTION_TYPES.RECOMMENDED;
    const isAddon = item.selectionType === SELECTION_TYPES.ADDON;

    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const lineTotal = qty * price;

    // In read-only mode, dim items the customer didn't approve.
    const isDimmed = readOnly && (
        finalState === 'declined-by-customer' || finalState === 'parent-declined'
    );

    const isToggleable = !readOnly && (isRecommended || isAddon);

    return (
        <div className={`flex items-start gap-3 py-3 ${isIncluded ? 'pl-8 md:pl-12' : ''} ${isDimmed ? 'opacity-40' : ''}`}>
            {/* Left: checkbox / lock / link icon */}
            <div className="flex-shrink-0 mt-0.5">
                {isToggleable ? (
                    <button
                        type="button"
                        onClick={() => onToggle(item.itemId)}
                        aria-label={isSelected ? 'Remove item' : 'Add item'}
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected
                                ? 'bg-green-600 border-green-600 text-white'
                                : 'bg-white border-gray-300 hover:border-green-400'
                        }`}
                    >
                        {isSelected && <Check size={14} strokeWidth={3} />}
                    </button>
                ) : isIncluded ? (
                    <div className="w-6 h-6 flex items-center justify-center text-purple-400">
                        <Link2 size={14} />
                    </div>
                ) : (
                    <div className="w-6 h-6 flex items-center justify-center text-gray-400" title="Required">
                        <Lock size={14} />
                    </div>
                )}
            </div>

            {/* Middle: name + description + selection badge */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`break-words ${LINE_NAME}`}>{item.name || 'Untitled item'}</span>
                    {isRecommended && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            <Star size={10} /> Recommended
                        </span>
                    )}
                    {isAddon && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                            <Plus size={10} /> Optional
                        </span>
                    )}
                    {isIncluded && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                            Included
                        </span>
                    )}
                </div>
                {item.description && (
                    <span className={LINE_DESC}><LineDescription text={item.description} /></span>
                )}
                <p className="text-xs text-gray-500 mt-0.5">
                    {qty} × {formatCurrency(price, currencyCode)}
                </p>
            </div>

            {/* Right: line total */}
            <div className="flex-shrink-0 text-right">
                {isIncluded && lineTotal === 0 ? (
                    <span className="text-xs text-gray-400 italic">included</span>
                ) : (
                    <span className="font-semibold text-gray-900">{formatCurrency(lineTotal, currencyCode)}</span>
                )}
            </div>
        </div>
    );
};


// ════════════════════════════════════════════════════════════════════
//   Main component — PortalQuoteDetail
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
//   DepositCard — r1-dep-portalui-6d2b8f39 (Seam 7)
// ════════════════════════════════════════════════════════════════════
//
// The post-approval deposit surface. Renders one of four states:
//
//   processing — back from Checkout, webhook not landed yet. The redirect is
//                cosmetic; the parent's onSnapshot is what actually confirms.
//   gated      — AwaitingDeposit. Paying is what completes the approval, so
//                this is the loud amber one.
//   optional   — Approved already; the deposit is a nudge, not a blocker.
//   cancelled  — they backed out of Checkout. Deliberately low-key: nothing
//                has gone wrong and the estimate is unharmed.
//
// payhide-7e4c1b05 — what a customer may see from a failed callable. A deliberate server
// refusal (an HttpsError with a real code) is hand-written customer copy, so it shows. A bare
// 'internal' / 'unknown' / 'unavailable' (an unexpected server error, or the device being
// offline) or a plain client Error shows the fallback instead of technical text.
const customerSafeMessage = (e, fallback) =>
    (typeof e?.code === 'string' && e.code.startsWith('functions/')
        && !['functions/internal', 'functions/unknown', 'functions/unavailable'].includes(e.code)
        && e.message)
        ? e.message
        : fallback;

const DepositCard = ({
    gated, amount, currencyCode, processing, timedOut, cancelled,
    onPay, paying, docWord,
    // SEAM 1d VERIFIER: pqd-depositcard-props-8f2a6d13
    // stripeReady defaults TRUE so nothing changes until we KNOW the org can't
    // take the payment online. When false, we render NO Pay button at all —
    // just contact copy — because the deposit is collected offline by design.
    stripeReady = true, companyName,
}) => {
    if (processing) {
        return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                    <Loader2 size={20} className="text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
                    <div>
                        <p className="text-sm font-semibold text-blue-900">Processing your payment…</p>
                        <p className="text-sm text-blue-800 mt-1">
                            {timedOut
                                ? 'This is taking a little longer than usual. Your payment went through — this page will update as soon as it’s confirmed. You can safely leave and come back.'
                                : 'Hang tight — we’re confirming your deposit with the payment provider. This page will update automatically.'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!stripeReady) {
        // SEAM 1d VERIFIER: pqd-depositcard-offline-3e7b1c94
        // No online deposit at this org — by design, not an outage. State the
        // deposit as a real requirement (gated) or a request (optional) to
        // settle with the business directly; never render a Pay button that
        // can't work. This is the guard that made Seam 1a necessary; it stays.
        return (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                    <CreditCard size={20} className="text-slate-500 flex-shrink-0 mt-0.5" />
                    <div>
                        {gated ? (
                            <>
                                <p className="text-sm font-semibold text-slate-900">A deposit of {formatCurrency(amount, currencyCode)} is required for this {docWord.toLowerCase()}.</p>
                                <p className="text-sm text-slate-700 mt-1">
                                    Please contact {companyName || 'the business'} to arrange payment. Your signature has been saved.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-semibold text-slate-900">A deposit of {formatCurrency(amount, currencyCode)} was requested for this {docWord.toLowerCase()}.</p>
                                <p className="text-sm text-slate-700 mt-1">
                                    You can arrange it with {companyName || 'the business'} directly.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const tone = gated
        ? { box: 'bg-amber-50 border-amber-200', head: 'text-amber-900', body: 'text-amber-800', icon: 'text-amber-600' }
        : { box: 'bg-slate-50 border-slate-200', head: 'text-slate-900', body: 'text-slate-700', icon: 'text-slate-500' };

    return (
        <div className={`border rounded-lg p-4 mt-4 ${tone.box}`}>
            {cancelled && (
                <p className={`text-sm mb-3 pb-3 border-b ${tone.body} ${gated ? 'border-amber-200' : 'border-slate-200'}`}>
                    Payment cancelled — nothing was charged. You can pay whenever you’re ready.
                </p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                    <CreditCard size={20} className={`flex-shrink-0 mt-0.5 ${tone.icon}`} />
                    <div>
                        <p className={`text-sm font-semibold ${tone.head}`}>
                            {gated
                                ? `Almost done — pay your deposit to complete your approval`
                                : `Deposit requested: ${formatCurrency(amount, currencyCode)}`}
                        </p>
                        <p className={`text-sm mt-1 ${tone.body}`}>
                            {gated
                                ? <>A deposit of <span className="font-semibold">{formatCurrency(amount, currencyCode)}</span> is required before this {docWord.toLowerCase()} is approved. We’ve saved your signature — you just need to pay.</>
                                : <>This {docWord.toLowerCase()} is already approved. You can pay the deposit now or arrange it with the business directly.</>}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onPay}
                    disabled={paying}
                    className={`flex-shrink-0 px-5 py-2.5 rounded-md font-bold text-sm shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
                        gated
                            ? 'bg-amber-600 text-white hover:bg-amber-700'
                            : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                    }`}
                >
                    {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                    {paying ? 'Opening payment…' : `Pay Deposit (${formatCurrency(amount, currencyCode)})`}
                </button>
            </div>
        </div>
    );
};


const PortalQuoteDetail = () => {
    const { quoteId } = useParams();
    const navigate = useNavigate();

    const [quote, setQuote] = useState(null);
    const [orgTermDefault, setOrgTermDefault] = useState('quote'); // r1-p3-docterm-pqdetail-c4f8a1e9 — org-default term for aggregate labels
    const [orgInfo, setOrgInfo] = useState({ companyName: '', logoUrl: '', currency: 'USD' });
    const [latestApproval, setLatestApproval] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    // pdraft-5c8e1a74 — the rules refused this estimate (a Draft, or not this
    // customer's). Rendered as a calm "not available yet" panel, not an error,
    // and the same panel for both causes so it never reveals what exists.
    const [unavailable, setUnavailable] = useState(false);

    const [selectedItemIds, setSelectedItemIds] = useState(new Set());
    const pageLoadedAtRef = useRef(Date.now());

    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [declineModalOpen, setDeclineModalOpen] = useState(false);
    const [requestChangesModalOpen, setRequestChangesModalOpen] = useState(false);

    const [processing, setProcessing] = useState(false);
    const [actionError, setActionError] = useState('');
    // payhide-7e4c1b05 — the inline error banner sits below the line items; bring it into
    // view when it appears so a failed click is never silent.
    const actionErrorBannerRef = useRef(null);
    const actionErrorInline = !!actionError && !signatureModalOpen && !declineModalOpen && !requestChangesModalOpen;
    useEffect(() => {
        if (actionErrorInline && actionErrorBannerRef.current?.scrollIntoView) {
            actionErrorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [actionErrorInline, actionError]);
    // r1-p6-portalpdfbtn-3c7a8e52 — customer quote-PDF download
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    // r1-dep-portalui-6d2b8f39 — Seam 7 deposit state.
    // orgDepositRequiredDefault rides along on the org-doc read this page already
    // does for terminology (no extra read). Only used for PRE-approval messaging —
    // after approval the server-written status is the truth (see isAwaitingDeposit).
    const [orgDepositRequiredDefault, setOrgDepositRequiredDefault] = useState(undefined);
    // SEAM 1b VERIFIER: pqd-orgstripeready-state-c9d0e1f2
    // Default TRUE so we never flash a false "payment unavailable" before the
    // org doc resolves. The server remains the real gate; this only suppresses a
    // Pay button that was never going to work once we KNOW the org isn't ready.
    const [orgStripeReady, setOrgStripeReady] = useState(true);
    const [payingDeposit, setPayingDeposit] = useState(false);
    // Separate from `processing` because handleApproveSubmit's finally clears that
    // one even on the redirect path — this keeps the modal disabled through the beat
    // between window.location.assign and the browser actually leaving.
    const [redirectingToPayment, setRedirectingToPayment] = useState(false);
    // The Stripe redirect is cosmetic — it only decides whether we show the
    // "processing…" state on arrival. The webhook is what actually confirms.
    const [searchParams, setSearchParams] = useSearchParams();
    const depositReturn = searchParams.get('deposit'); // 'success' | 'cancelled' | null
    const [depositWaitTimedOut, setDepositWaitTimedOut] = useState(false);

    // r1-1bc-notes-state-8b3f1a6d — customer-facing notes+photos for this quote.
    // Fetched via the getCustomerQuoteNotes callable because portal clients can't
    // sign notes-media Storage URLs client-side (storage.rules blocks them).
    const [notesItems, setNotesItems] = useState([]);

    // qview-6b2d4e91 — Seam 10. Reports ONE view per page load once the
    // estimate is on screen, the tab has been visible ~5 s and the person has
    // interacted. Silent; never on a staff-preview browser. See the hook.
    usePortalViewReport(quoteId, !loading && !loadError && !!quote);

    // ── Load quote + org info + latest approval (if approved) ──────
    useEffect(() => {
        const fetchAll = async () => {
            // r1-p3-docterm-pqdetail-c4f8a1e9 — aggregate word for load-error strings (default until org read resolves)
            const aggTermWord = resolveDocTerminology(orgTermDefault).word;
            const user = auth.currentUser;
            if (!user || !quoteId) {
                setLoadError(`Not signed in or ${aggTermWord.toLowerCase()} not specified.`);
                setLoading(false);
                return;
            }
            try {
                const tokenResult = await user.getIdTokenResult();
                const orgId = tokenResult.claims.organizationId;
                const customerId = tokenResult.claims.customerId;
                if (!orgId) throw new Error('No Organization ID found in token.');

                // Fetch quote + orgInfo in parallel.
                // o3-portalproj-9c4e7b21 — this used to be a THREE-way Promise.all whose
                // third entry was a raw organizations/{orgId} read. That read shipped the
                // whole org doc to the customer, and because one rejection rejects the
                // whole Promise.all, it was also the only thing that could blank this page
                // outright. All three values it supplied now come from the projection.
                // x7portal-6e1a3d58 — X7 (#29): the estimate (and its latest approval receipt) come
                // from getPortalQuotes, field-allowlisted on the server; the portal can no longer read
                // the estimate document itself. An estimate this customer may not see still rejects
                // with code 'permission-denied', so the panel below is unchanged.
                const getOrgInfo = httpsCallable(functions, 'getPublicOrganizationInfo');
                // pdraft-5c8e1a74 — allSettled, not all: a refused estimate must not
                // take the org info down with it, because the "not available" panel
                // names the company. An org-info failure still fails the page as before.
                const [quoteRes, orgRes] = await Promise.allSettled([
                    fetchPortalQuote(quoteId),
                    getOrgInfo(),
                ]);
                if (quoteRes.status === 'rejected' && quoteRes.reason?.code === 'permission-denied') {
                    if (orgRes.status === 'fulfilled') {
                        if (orgRes.value.data?.quoteTerminologyDefault) {
                            setOrgTermDefault(orgRes.value.data.quoteTerminologyDefault);
                        }
                        setOrgInfo(orgRes.value.data || {});
                    }
                    setUnavailable(quoteRes.reason?.reason === 'deleted' ? 'deleted' : true); // estdel-f9ac8ba8
                    return;
                }
                if (quoteRes.status === 'rejected') throw quoteRes.reason;
                if (orgRes.status === 'rejected') throw orgRes.reason;
                const quoteSnap = quoteRes.value.snap;
                const loadedApproval = quoteRes.value.latestApproval; // x7portal-6e1a3d58
                const orgResult = orgRes.value;
                // r1-p3-docterm-pqdetail-c4f8a1e9 — aggregate word (back links + load errors).
                // Truthy guard kept so an absent value leaves the 'quote' default.
                if (orgResult.data?.quoteTerminologyDefault) {
                    setOrgTermDefault(orgResult.data.quoteTerminologyDefault);
                }
                // r1-dep-portalui-6d2b8f39 — left as undefined when absent so
                // resolveDepositRequired's ?? chain can fall through to its own default
                // rather than us inventing one. The projection omits the key entirely in
                // that case (it does NOT send false), which preserves exactly that.
                setOrgDepositRequiredDefault(orgResult.data?.depositRequiredDefault);
                // pqd-orgstripeready-state-c9d0e1f2 — server-computed boolean now. The raw
                // stripeConnectedAccountId / stripeAccountId never reach the browser.
                // Equivalent to the old isOrgStripeReady(orgDoc): on a successful read with
                // the fields absent that returned false too.
                setOrgStripeReady(!!orgResult.data?.stripeReady);

                if (!quoteSnap.exists()) {
                    setLoadError(`${aggTermWord} not found.`);
                    setLoading(false);
                    return;
                }
                const data = quoteSnap.data();
                if (data.customerId && data.customerId !== customerId) {
                    // pdraft-5c8e1a74 — same calm panel as a rules refusal.
                    setOrgInfo(orgResult.data || {});
                    setUnavailable(true);
                    setLoading(false);
                    return;
                }

                setQuote({ id: quoteSnap.id, ...data, orgId });
                setOrgInfo(orgResult.data || {});

                // Initialize selected set: all Recommended items pre-checked.
                const initialSelected = new Set();
                for (const item of (data.lineItems || [])) {
                    if (item.selectionType === SELECTION_TYPES.RECOMMENDED && item.itemId) {
                        initialSelected.add(item.itemId);
                    }
                }
                setSelectedItemIds(initialSelected);

                // If approved, fetch the most recent approval doc for the read-only view.
                // r1-dep-portalui-6d2b8f39 — AwaitingDeposit is also post-signature:
                // the customer HAS signed, they just haven't paid. Without this they'd
                // see the pay step with no record of the signature they just gave.
                if ((isApprovedStatus(data.status) || data.status === QUOTE_STATUSES.AWAITING_DEPOSIT) && loadedApproval) {
                    setLatestApproval(loadedApproval); // x7portal-6e1a3d58 — came with the estimate
                }
            } catch (e) {
                console.error('PortalQuoteDetail: failed to load', e);
                setLoadError(`Could not load ${aggTermWord.toLowerCase()}. Please refresh and try again.`);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
        pageLoadedAtRef.current = Date.now();
    }, [quoteId]);

    // r1-1bc-notes-fetch-b8f3a1c6 — load customer-facing notes+photos once the
    // quote is known. Separate, non-blocking effect: notes are supplementary, so a
    // failure just leaves the section hidden (the quote still renders). No status
    // gate — this runs for Sent quotes too, so photos show before approval.
    useEffect(() => {
        if (!quote?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const notesFn = httpsCallable(functions, 'getCustomerQuoteNotes');
                const result = await notesFn({ quoteId: quote.id });
                if (!cancelled) setNotesItems(result?.data?.items || []);
            } catch (e) {
                console.warn('PortalQuoteDetail: could not load notes', e);
                if (!cancelled) setNotesItems([]);
            }
        })();
        return () => { cancelled = true; };
    }, [quote?.id]);

    // ── Derived state ──────────────────────────────────────────────
    const status = quote?.status || '';
    const normalizedStatus = status.toLowerCase();
    const isSent = normalizedStatus === 'sent';
    const isApproved = isApprovedStatus(status);
    const isDeclined = isDeclinedStatus(status);
    const isChangesRequested = isChangesRequestedStatus(status);

    // r1-dep-portalui-6d2b8f39 — GAP #4. AwaitingDeposit matched none of the four
    // predicates above, so a customer who had signed still landed on the stale
    // approve view. Strict compare on the canonical constant is deliberate: unlike
    // 'Sent' (which has legacy casing variants in old data) AwaitingDeposit is new
    // and only ever written by approveQuoteV2 as the constant — the server compares
    // it strictly too.
    const isAwaitingDeposit = status === QUOTE_STATUSES.AWAITING_DEPOSIT;
    // Both states mean "the customer has signed": items lock, totals come from the
    // server's approved figures, and the signature receipt shows.
    const isPostApproval = isApproved || isAwaitingDeposit;

    const lineItems = quote?.lineItems || [];
    const orderedItems = useMemo(() => orderItemsForDisplay(lineItems), [lineItems]);
    const legacyMode = useMemo(() => isLegacyQuote(lineItems), [lineItems]);

    const currencyCode = orgInfo.currency || 'USD';

    // Live calculations as customer toggles selections
    // taxlines2-3e0b16a4 — this is the LOCAL figure (today's proportional scaling); `liveCalc` below
    // swaps in the server's answer for a Settings › Taxes company.
    const localCalc = useMemo(() => {
        const state = computeApprovalState(lineItems, selectedItemIds);
        const approvedTax = computeProportionalTax(
            Number(quote?.taxAmount) || 0,
            state.originalTaxableSubtotal,
            state.approvedTaxableSubtotal,
        );
        const approvedTotal = Math.round((state.approvedSubtotal + approvedTax) * 100) / 100;
        return {
            ...state,
            approvedTaxAmount: approvedTax,
            approvedTotal,
        };
    }, [lineItems, selectedItemIds, quote?.taxAmount]);

    // taxlines2-3e0b16a4 — S2: the approval preview.
    //  • Settings › Taxes OFF (quote.taxCodesOn !== true — getPortalQuotes sends the server's own switch):
    //    no call, nothing waits, liveCalc IS the local figures — the page is exactly as before S2.
    //  • ON: the approval is worked out from the company's tax codes as they are now (server-only data), so
    //    the figures shown are ALWAYS the server's: previewQuoteApproval runs exactly what approveQuoteV2
    //    runs and writes nothing. Re-asked (debounced) whenever the ticks change; Approve waits until the
    //    figures shown match the ticks. No answer within PREVIEW_TIMEOUT_MS, or a failure → PREVIEW_FAILED_TEXT
    //    and Approve stays disabled. Never the local estimate of the tax or the total.
    //  • UNKNOWN (taxexempt3-8c4f2a1d — taxCodesOn === null: the server couldn't read the tax settings) is
    //    treated as ON: server figures only, never the local estimate.
    const previewOn = isSent && (quote?.taxCodesOn === true || quote?.taxCodesOn === null);
    const selectionKey = Array.from(selectedItemIds).sort().join('|');
    const [serverPreview, setServerPreview] = useState(null); // { key, approvedSubtotal, approvedTaxAmount, approvedTotal, approvedTaxParts }
    const [previewFailed, setPreviewFailed] = useState(false);
    const previewAskedRef = useRef(false);
    const previewOrgId = quote?.orgId;
    const previewQuoteId = quote?.id;
    useEffect(() => {
        if (!previewOn || previewFailed || !previewOrgId || !previewQuoteId) return undefined;
        let cancelled = false;
        const ids = selectionKey ? selectionKey.split('|') : [];
        const delay = previewAskedRef.current ? 300 : 0;
        previewAskedRef.current = true;
        const timer = setTimeout(async () => {
            let limit;
            try {
                const previewFn = httpsCallable(functions, 'previewQuoteApproval');
                const r = await Promise.race([
                    previewFn({ orgId: previewOrgId, quoteId: previewQuoteId, selectedItemIds: ids }),
                    new Promise((_, reject) => { limit = setTimeout(() => reject(new Error('previewQuoteApproval: no answer in time')), PREVIEW_TIMEOUT_MS); }),
                ]);
                if (cancelled) return;
                const d = r && r.data ? r.data : {};
                const fig = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
                if (fig(d.approvedSubtotal) === null || fig(d.approvedTaxAmount) === null || fig(d.approvedTotal) === null) throw new Error('previewQuoteApproval: incomplete answer');
                setServerPreview({ key: selectionKey, approvedSubtotal: d.approvedSubtotal, approvedTaxAmount: d.approvedTaxAmount, approvedTotal: d.approvedTotal, approvedTaxParts: Array.isArray(d.approvedTaxParts) ? d.approvedTaxParts : null });
            } catch (e) {
                if (!cancelled) { console.warn('PortalQuoteDetail: approval preview failed', e); setPreviewFailed(true); }
            } finally {
                clearTimeout(limit);
            }
        }, delay);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [previewOn, previewFailed, previewOrgId, previewQuoteId, selectionKey]);
    const previewFresh = previewOn && !previewFailed && !!serverPreview && serverPreview.key === selectionKey;
    const previewPending = previewOn && !previewFresh; // waiting for the server, or it failed: Approve stays disabled
    const liveCalc = useMemo(() => (!previewOn ? localCalc
        : previewFresh
            ? { ...localCalc, approvedSubtotal: serverPreview.approvedSubtotal, approvedTaxAmount: serverPreview.approvedTaxAmount, approvedTotal: serverPreview.approvedTotal }
            : { ...localCalc, approvedTaxAmount: null, approvedTotal: null }), [previewOn, previewFresh, localCalc, serverPreview]);

    // ── Deposit derived state (r1-dep-portalui-6d2b8f39) ───────────
    //
    // Trust split, mirroring the schema's:
    //   PRE-approval  — deposit.type/value are owner config; we resolve the amount
    //                   client-side off the customer's LIVE selection so the preview
    //                   tracks what they've ticked. Same math the server will run.
    //   POST-approval — deposit.amountResolved / depositStatus are CF-written money
    //                   truth. Never recomputed here.
    const depositCfg = quote?.deposit;
    const hasDepositConfig = isValidDepositType(depositCfg?.type);

    // Live preview of what the deposit WILL be, for the Sent state.
    const depositPreviewAmount = useMemo(
        () => (hasDepositConfig && liveCalc.approvedTotal !== null ? resolveDepositAmount(depositCfg.type, depositCfg.value, liveCalc.approvedTotal) : 0), // taxlines2-3e0b16a4 — null = the server's total hasn't come yet
        [hasDepositConfig, depositCfg?.type, depositCfg?.value, liveCalc.approvedTotal],
    );
    const hasDepositPreview = hasDepositConfig && depositPreviewAmount > 0;

    // Whether paying gates approval. Only meaningful BEFORE approval — afterwards the
    // status carries it (AwaitingDeposit ⇒ was required; Approved+unpaid ⇒ optional),
    // so we never second-guess the server's decision with a client recompute.
    const depositRequired = resolveDepositRequired(quote?.depositRequired, orgDepositRequiredDefault);

    // Server-written truth, for the post-approval states.
    const depositAmount = Number(depositCfg?.amountResolved) || 0;
    const depositUnpaid = quote?.depositStatus === DEPOSIT_STATUSES.UNPAID && depositAmount > 0;
    // RECORDED = an operator logged an offline payment (Seam 4). Display-only here,
    // but treating it as paid now means this page is already correct when Seam 4 lands.
    const depositPaid = quote?.depositStatus === DEPOSIT_STATUSES.PAID
                     || quote?.depositStatus === DEPOSIT_STATUSES.RECORDED;

    // 1c-portal-vars-2f8b6c3a — Seam 1c: an offline-recorded deposit stores its
    // timestamp in deposit.recordedAt (NOT paidAt), and may record a partial
    // deposit.recordedAmount. The receipt reads whichever timestamp is present so
    // an offline deposit still shows a date, and shows the amount that ACTUALLY
    // arrived — not the agreed figure — when the two differ. Stripe deposits set
    // neither new field, so both reads fall through to the original values.
    const depositReceiptAmount = Number(depositCfg?.recordedAmount ?? depositCfg?.amountResolved) || 0;
    const depositSettledAt = depositCfg?.paidAt || depositCfg?.recordedAt;

    // Returned from Checkout but the webhook hasn't landed yet → "processing…".
    const depositProcessing = depositReturn === 'success' && depositUnpaid;

    // ── Expiry ──────────────────────────────────────────  qexp-6f2a9d38-portal
    // Seam 5. The "non-blocking warning per Phase 2 design" is SUPERSEDED: an
    // expired estimate can no longer be approved online, here or on the server
    // (approveQuoteV2 §4b is the guard that actually holds).
    //
    // TWO BUGS FIXED BY MOVING TO THE SHARED RULE:
    //   1. This used to compare INSTANTS. expiresAt is stored as local midnight,
    //      so an estimate read "expired" for the whole of its own expiry day
    //      while the PDF the customer was holding said "VALID UNTIL <that day>".
    //      The shared rule makes an estimate valid THROUGH its expiry date.
    //   2. The day was decided in the customer's browser timezone. It now comes
    //      from the org's, via the getPublicOrganizationInfo projection — the
    //      same timezone the PDF and the quote email already print in.
    const expiry = useMemo(
        () => resolveExpiryState(quote, { timeZone: orgInfo.timezone }),
        [quote, orgInfo.timezone]
    );
    const isExpired = expiry.state === EXPIRY_STATES.EXPIRED;

    // Every date this page shows the customer is formatted in the ORG's timezone,
    // so the "Expires" line, the expired banner and the PDF's "VALID UNTIL" line
    // can never print three different days for the same estimate.
    const fmtOrgDate = useCallback((value, opts) => {
        const d = toExpiryDate(value);
        if (!d) return 'N/A';
        const f = opts || { year: 'numeric', month: 'long', day: 'numeric' };
        try {
            return d.toLocaleDateString('en-US', { ...f, timeZone: orgInfo.timezone || 'America/Toronto' });
        } catch (_tzErr) {
            return d.toLocaleDateString('en-US', { ...f, timeZone: 'America/Toronto' });
        }
    }, [orgInfo.timezone]);
    const expiredOnText = expiry.expiresAt ? fmtOrgDate(expiry.expiresAt) : '';

    // ── Live deposit confirmation (r1-dep-portalui-6d2b8f39) ───────
    //
    // The page loads via a one-shot getDoc; this adds a narrow listener that runs
    // ONLY while a deposit is outstanding, then unsubscribes once it's settled.
    // It writes back into the same `quote` state, so every render branch — status
    // badge, banners, totals, the pay card — flips on its own when the webhook
    // lands. No refresh, no polling, no duplicated status logic.
    //
    // Keyed on depositStatus rather than on ?deposit=success deliberately: the
    // webhook is the source of truth, so this must also catch a payment confirmed
    // in another tab, a customer sitting on the page when a reminder link is paid,
    // and (once Seam 4 ships) an operator recording an offline deposit.
    useEffect(() => {
        if (!quote?.orgId || !quote?.id) return;
        if (quote.depositStatus !== DEPOSIT_STATUSES.UNPAID) return;

        // x7portal-6e1a3d58 — X7 (#29): the portal can no longer listen to the estimate document, so
        // this asks getPortalQuotes every 5 seconds instead (only while the deposit is unpaid and the
        // tab is visible; it stops after 20 minutes — a reload starts it again).
        let stopped = false;
        const startedAt = Date.now();
        const timer = setInterval(async () => {
            if (stopped || document.visibilityState === 'hidden') return;
            if (Date.now() - startedAt > 20 * 60 * 1000) { clearInterval(timer); return; }
            try {
                const { snap } = await fetchPortalQuote(quote.id);
                if (!stopped && snap.exists() && snap.data().depositStatus !== quote.depositStatus) {
                    setQuote({ id: snap.id, ...snap.data(), orgId: quote.orgId });
                }
            } catch (err) { console.warn('PortalQuoteDetail: deposit watch failed', err); }
        }, 5000);
        return () => { stopped = true; clearInterval(timer); };
    }, [quote?.orgId, quote?.id, quote?.depositStatus]);

    // The approval receipt isn't fetched on load for an estimate that was still
    // AwaitingDeposit; once the webhook flips it to Approved, pull it in so the
    // signature shows without a refresh.
    useEffect(() => {
        if (!isApproved || latestApproval || !quote?.orgId || !quote?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const { latestApproval: fetched } = await fetchPortalQuote(quote.id); // x7portal-6e1a3d58
                if (!cancelled && fetched) setLatestApproval(fetched);
            } catch (_) { /* non-blocking — the quote-level summary still renders */ }
        })();
        return () => { cancelled = true; };
    }, [isApproved, latestApproval, quote?.orgId, quote?.id]);

    // Soft timeout so a slow webhook reads as "still working" rather than a hang.
    useEffect(() => {
        if (!depositProcessing) { setDepositWaitTimedOut(false); return; }
        const t = setTimeout(() => setDepositWaitTimedOut(true), 25000);
        return () => clearTimeout(t);
    }, [depositProcessing]);

    // Once the deposit is settled, drop ?deposit= from the URL so a refresh or a
    // shared link doesn't resurrect the processing/cancelled banner.
    //
    // The `quote` guard is load-bearing: until the doc loads, depositUnpaid is false
    // simply because we don't know yet. Without it we'd strip the param on the first
    // render after returning from Checkout and the customer would never see the
    // "Processing your payment…" state at all.
    useEffect(() => {
        if (!quote) return;
        if (depositReturn && !depositUnpaid) {
            const next = new URLSearchParams(searchParams);
            next.delete('deposit');
            setSearchParams(next, { replace: true });
        }
    }, [quote, depositReturn, depositUnpaid, searchParams, setSearchParams]);

    // pdraft-5c8e1a74 — the ONE wording for "the customer can't see this
    // estimate yet": the unavailable panel and a refused decline both use it.
    const unavailableText = (word) =>
        `This ${word.toLowerCase()} isn't available right now. ${orgInfo.companyName || 'The business'} may still be working on it, and they'll send it to you when it's ready.`;

    // ── Handlers ────────────────────────────────────────────────────
    const toggleItem = useCallback((itemId) => {
        setSelectedItemIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    }, []);

    const handleApproveClick = () => {
        if (previewPending) return; // taxlines2-3e0b16a4 — wait until the total shown matches the ticks
        // Soft guard: if nothing would actually be approved, suggest declining instead.
        if (liveCalc.approvedItemIds.length === 0) {
            const proceed = window.confirm(
                "You haven't selected any items to approve. Would you like to decline the quote instead?"
            );
            if (proceed) {
                setDeclineModalOpen(true);
            }
            return;
        }
        setActionError('');
        setSignatureModalOpen(true);
    };

    const handleApproveSubmit = async (signature) => {
        if (!quote) return;
        setProcessing(true);
        setActionError('');
        try {
            const approveFn = httpsCallable(functions, 'approveQuoteV2');
            const approvalDurationMs = Date.now() - pageLoadedAtRef.current;
            const result = await approveFn({
                orgId: quote.orgId,
                quoteId: quote.id,
                selectedItemIds: Array.from(selectedItemIds),
                signature,
                customerNote: null,
                approvalDurationMs,
                // s8audit-7c2e5b94 — S8b P2: honest-only signal that staff were signed in on this
                // browser when the portal link was opened. Recorded server-side for the Owner; not proof.
                staffPreviewBrowser: isStaffPreviewBrowser(),
            });
            if (result?.data?.success) {
                // r1-dep-portalui-6d2b8f39 — REQUIRED deposit: the server held the
                // estimate at AwaitingDeposit and already minted a Checkout link, so
                // send them straight there rather than making them click again. Note
                // the field is `depositUrl` here; getDepositPaymentLink returns `url`.
                //
                // We branch on the SERVER's status, not on the client's depositRequired
                // — the server decided the hold, so it decides the redirect.
                //
                // depositUrl is null when Stripe's mint failed (approveQuoteV2 swallows
                // that and still reports success, because the approval is durable). We
                // then fall through to the AwaitingDeposit view, whose Pay Deposit
                // button mints a fresh link. Either way nobody is stranded.
                const { status: newStatus, depositUrl } = result.data;
                if (newStatus === QUOTE_STATUSES.AWAITING_DEPOSIT && depositUrl) {
                    setRedirectingToPayment(true); // stays set — the finally below clears `processing`
                    window.location.assign(depositUrl);
                    return;
                }

                // Refresh from Firestore so the read-only Approved view renders
                // from the canonical server-written totals (avoiding any tiny
                // client/server math drift due to rounding edge cases).
                // x7portal-6e1a3d58 — the estimate and its new approval receipt in one call.
                try {
                    const { snap: refreshed, latestApproval: fetched } = await fetchPortalQuote(quote.id);
                    if (refreshed.exists()) {
                        setQuote({ id: refreshed.id, ...refreshed.data(), orgId: quote.orgId });
                    }
                    if (fetched) setLatestApproval(fetched);
                } catch (_) { /* non-blocking */ }
                setSignatureModalOpen(false);
            } else {
                setActionError('The server did not confirm the approval. Please try again.');
            }
        } catch (e) {
            console.error('approveQuoteV2 failed', e);
            setActionError(e.message || 'Could not submit your approval. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    // r1-dep-portalui-6d2b8f39 — Pay Deposit. Always mints a FRESH Checkout session
    // rather than reusing the URL approveQuoteV2 handed back: sessions expire in 24h,
    // and the returned URL is gone after a reload anyway. One code path covers the
    // post-approval click, a reload, a return days later, and a cancel-then-retry.
    const handlePayDeposit = async () => {
        if (!quote) return;
        // SEAM 1b VERIFIER: pqd-paydeposit-shortcircuit-e1f2a3b4
        // Belt-and-suspenders: the button is already disabled when !orgStripeReady,
        // but guard the handler too in case it's fired before state settles. Show
        // the same friendly copy instead of calling a callable that will reject.
        if (!orgStripeReady) {
            setActionError(`Online deposit payment isn’t available right now. Please contact ${orgInfo.companyName || 'the business'} to arrange your deposit.`);
            return;
        }
        setPayingDeposit(true);
        setActionError('');
        try {
            const linkFn = httpsCallable(functions, 'getDepositPaymentLink');
            const result = await linkFn({ orgId: quote.orgId, quoteId: quote.id });
            const url = result?.data?.url; // `url` here; `depositUrl` on approveQuoteV2
            if (!url) throw new Error('No payment link was returned.');
            // Top-level redirect to hosted Stripe Checkout. (The <a target="_blank">
            // pattern used for PDF downloads is a workaround for CORS on signed GCS
            // URLs — it doesn't apply here.) Deliberately not clearing payingDeposit:
            // we're leaving the page and the button should stay disabled until we do.
            window.location.assign(url);
        } catch (e) {
            console.error('getDepositPaymentLink failed', e);
            setActionError(customerSafeMessage(e, 'Could not start the deposit payment. Please try again.'));
            setPayingDeposit(false);
        }
    };

    const handleDeclineConfirm = async () => {
        if (!quote) return;
        setProcessing(true);
        setActionError('');
        try {
            // No dedicated decline CF — we use a direct status update, which still
            // fires the existing onQuoteStatusChanged trigger to handle notifications.
            // (Phase 2 deliberately kept this as a thin path since there's no audit
            // trail to write for a decline.)
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            const quoteRef = doc(db, `organizations/${quote.orgId}/quotes`, quote.id);
            await updateDoc(quoteRef, {
                status: 'Declined',
                declinedAt: serverTimestamp(),
                lastUpdated: serverTimestamp(),
                // lastact-w4 — the customer declined. This is the ONLY client path
                // where a portal customer writes a quote directly, so it is the one
                // place lastActivityAt needs the firestore.rules whitelist to permit
                // it (r1-lastact-portal-4e7b2c19) — hasOnly() rejects the WHOLE write
                // on any unlisted key, so this line and that rule ship together.
                // serverTimestamp() is also load-bearing, not stylistic: the rule
                // pins the value to request.time, so a literal Date would be denied.
                lastActivityAt: serverTimestamp(),
                followUpRemindersEnabled: false,
            });
            // Refresh (x7portal-6e1a3d58 — through getPortalQuotes; the decline write itself is unchanged)
            const { snap: refreshed } = await fetchPortalQuote(quote.id);
            if (refreshed.exists()) {
                setQuote({ id: refreshed.id, ...refreshed.data(), orgId: quote.orgId });
            }
            setDeclineModalOpen(false);
        } catch (e) {
            console.error('decline failed', e);
            // pdraft-5c8e1a74 — refused because the estimate went back to Draft
            // while this page was open (or is no longer this customer's).
            if (e?.code === 'permission-denied') {
                setActionError(unavailableText(resolveDocTerminology(quote.docType).word));
            } else {
                setActionError(e.message || 'Could not decline the quote. Please try again.');
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleRequestChangesSubmit = async (note) => {
        if (!quote) return;
        setProcessing(true);
        setActionError('');
        try {
            const reqFn = httpsCallable(functions, 'requestQuoteChanges');
            const result = await reqFn({
                orgId: quote.orgId,
                quoteId: quote.id,
                customerNote: note,
                staffPreviewBrowser: isStaffPreviewBrowser(), // s8audit-7c2e5b94 — S8b P2
            });
            if (result?.data?.success) {
                const { snap: refreshed } = await fetchPortalQuote(quote.id); // x7portal-6e1a3d58
                if (refreshed.exists()) {
                    setQuote({ id: refreshed.id, ...refreshed.data(), orgId: quote.orgId });
                }
                setRequestChangesModalOpen(false);
            } else {
                setActionError('The server did not confirm. Please try again.');
            }
        } catch (e) {
            console.error('requestQuoteChanges failed', e);
            setActionError(e.message || 'Could not send your request. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    // r1-p6-portalpdfbtn-3c7a8e52 — customer downloads their quote as a PDF
    // (their copy / signed receipt). Calls downloadCustomerQuotePdf, fetches
    // the signed URL as a blob, triggers a direct file download.
    // docpdf-8a4c1e73 — getPortalQuotePdf returns the PDF's bytes; it is saved in this browser. Nothing is
    // stored on the server and there is no link (was downloadCustomerQuotePdf: a file + a 7-day link).
    const handleDownloadPdf = async () => {
        if (!quote?.id) return;
        setIsDownloadingPdf(true);
        setActionError('');
        try {
            if (!auth.currentUser) throw new Error('You must be logged in.');
            const result = await httpsCallable(functions, 'getPortalQuotePdf', { timeout: 120000 })({ quoteId: quote.id });
            downloadPdfBytes(result.data?.pdfBase64, result.data?.fileName);
        } catch (e) {
            console.error('Customer quote PDF download failed', e);
            setActionError(customerSafeMessage(e, 'Could not download the PDF. Please try again.'));
            setTimeout(() => setActionError(''), 5000);
        }
        setIsDownloadingPdf(false);
    };

    // r1-p3-docterm-pqdetail-c4f8a1e9 — aggregate word resolved before all render branches (error branch uses it too; no quote loaded there)
    const aggTerm = resolveDocTerminology(orgTermDefault);

    // ── Rendering ───────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="p-10 flex justify-center">
                <Loader2 className="animate-spin text-green-600" />
            </div>
        );
    }
    // pdraft-5c8e1a74 — calm grey panel, not the red error below.
    if (unavailable) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-slate-700">
                    <div className="flex items-start gap-3">
                        <Clock size={20} className="flex-shrink-0 mt-0.5 text-slate-500" />
                        <div>
                            <p className="font-semibold text-slate-900">{unavailable === 'deleted' ? deletedQuotePortalMessage({ docType: aggTerm.type }) : unavailableText(aggTerm.word)}</p>{/* estdel-f9ac8ba8 */}
                            <button onClick={() => navigate('/portal/quotes')} className="mt-2 text-sm underline">
                                Back to {aggTerm.wordPlural}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (loadError) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
                    <div className="flex items-start gap-3">
                        <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold">{loadError}</p>
                            <button onClick={() => navigate('/portal/quotes')} className="mt-2 text-sm underline">
                                Back to {aggTerm.wordPlural}{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (!quote) return null;

    // r1-p3-docterm-pqdetail-c4f8a1e9 — per-doc word (freeze) for the render body; aggTerm resolved above
    const docTerm = resolveDocTerminology(quote.docType);

    // r1-dep-portalui-6d2b8f39 — the signature-receipt card renders in two tones:
    // green once genuinely Approved, neutral slate while AwaitingDeposit (green
    // would tell the customer they're done when they still owe a deposit).
    const receipt = isAwaitingDeposit
        ? { label: 'text-slate-700', body: 'text-slate-800', strong: 'text-slate-900', line: 'border-slate-200' }
        : { label: 'text-green-700', body: 'text-green-800', strong: 'text-green-900', line: 'border-green-200' };

    // Determine display amounts based on whether we're in a post-approval view.
    // r1-dep-portalui-6d2b8f39 — isPostApproval, not isApproved: an AwaitingDeposit
    // estimate is signed, so it must show the APPROVED totals. Falling through to
    // quote.subtotal/total here would show the customer the full pre-selection
    // figures — not what they signed for, and not what the deposit is based on.
    // The quote doc's approved* fields are the same values approveQuoteV2 wrote, so
    // they're a correct fallback whenever the approval doc hasn't loaded.
    const displaySubtotal = isPostApproval && latestApproval?.totals
        ? latestApproval.totals.approvedSubtotal
        : (isSent ? liveCalc.approvedSubtotal : (isPostApproval ? (quote.approvedSubtotal ?? quote.subtotal) : quote.subtotal));
    const displayTax = isPostApproval && latestApproval?.totals
        ? latestApproval.totals.approvedTaxAmount
        : (isSent ? liveCalc.approvedTaxAmount : (isPostApproval ? (quote.approvedTaxAmount ?? quote.taxAmount ?? 0) : (quote.taxAmount || 0)));
    const displayTotal = isPostApproval && latestApproval?.totals
        ? latestApproval.totals.approvedTotal
        : (isSent ? liveCalc.approvedTotal : (isPostApproval ? (quote.approvedTotal ?? quote.total ?? 0) : (quote.total || 0)));

    const effectiveTaxPct = displaySubtotal > 0 ? ((displayTax / displaySubtotal) * 100).toFixed(1) : '0.0';
    // taxlines2-3e0b16a4 — one row per tax part, only when the parts add up to the tax shown; else
    // today's "Tax (x%)" row. Signed: the approved parts. Sent: the server preview's parts (Settings ›
    // Taxes companies only). Anything else: the estimate's own parts.
    const displayTaxRows = isPostApproval
        ? customerTaxRows({ approvedTaxParts: quote.approvedTaxParts, approvedTaxAmount: displayTax }, 'approved')
        : (isSent
            ? (previewFresh ? customerTaxRows({ approvedTaxParts: serverPreview.approvedTaxParts, approvedTaxAmount: displayTax }, 'approved') : null)
            : customerTaxRows({ taxParts: quote.taxParts, taxAmount: displayTax }, 'headline'));

    return (
        <div className="max-w-4xl mx-auto pb-32 md:pb-6">
            {/* Back link */}
            <div className="mb-4">
                <button
                    onClick={() => navigate('/portal/quotes')}
                    className="flex items-center text-gray-500 hover:text-gray-800 transition-colors"
                >
                    <ArrowLeft size={18} className="mr-1" /> Back to {aggTerm.wordPlural}{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                </button>
            </div>

            {/* ───────── HEADER CARD (logo + company info + status badge) ───────── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Logo + company info */}
                    <div className="flex flex-col md:flex-row md:items-center gap-4 flex-1">
                        {orgInfo.logoUrl && (
                            <img
                                src={orgInfo.logoUrl}
                                alt={`${orgInfo.companyName || 'Company'} logo`}
                                className="h-16 md:h-20 max-w-[200px] object-contain object-left"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                        )}
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                                {docTerm.word} #{quote.quoteNumber || quote.id.slice(0, 6).toUpperCase()}{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                            </p>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {orgInfo.companyName || `Service ${docTerm.word}`}{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                            </h1>
                            {(orgInfo.address1 || orgInfo.city || orgInfo.phone) && (
                                <div className="mt-1 text-sm text-gray-500">
                                    {orgInfo.address1 && <p>{orgInfo.address1}{orgInfo.address2 ? `, ${orgInfo.address2}` : ''}</p>}
                                    {(orgInfo.city || orgInfo.state || orgInfo.zip) && (
                                        <p>{[orgInfo.city, orgInfo.state, orgInfo.zip].filter(Boolean).join(', ')}</p>
                                    )}
                                    {orgInfo.phone && <p>{orgInfo.phone}</p>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Status badge + download */}
                    <div className="flex-shrink-0 flex flex-col items-end gap-2">
                        <button
                            onClick={handleDownloadPdf}
                            disabled={isDownloadingPdf}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDownloadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            {isDownloadingPdf ? 'Preparing...' : 'Download PDF'}
                        </button>
                        {isSent && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                                Awaiting your decision
                            </span>
                        )}
                        {/* r1-dep-portalui-6d2b8f39 — wording matches QuoteStatusBadge,
                            which the portal LIST view already renders, so list and
                            detail agree for the same estimate. */}
                        {isAwaitingDeposit && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                <CreditCard size={12} /> Awaiting Deposit
                            </span>
                        )}
                        {isApproved && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                                <Check size={12} /> Approved{depositPaid && depositAmount > 0 ? ' – deposit paid' : ''}
                            </span>
                        )}
                        {isDeclined && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                                <X size={12} /> Declined
                            </span>
                        )}
                        {isChangesRequested && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                <MessageSquare size={12} /> Changes requested
                            </span>
                        )}
                    </div>
                </div>

                {/* Date row */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5">
                        <Calendar size={14} />
                        Date: {quote.createdAt?.toDate ? quote.createdAt.toDate().toLocaleDateString() : 'N/A'}
                    </span>
                    {quote.expiresAt && (
                        <span className="flex items-center gap-1.5">
                            <Clock size={14} />
                            {/* qexp-6f2a9d38-portal — org timezone, so this line agrees
                                with the expired banner and with the PDF. */}
                            Expires: {fmtOrgDate(quote.expiresAt)}
                        </span>
                    )}
                </div>
            </div>

            {/* Expired — BLOCKING as of Seam 5 (qexp-6f2a9d38-portal). The old
                amber "you can still approve it" copy is gone: the actions below
                are disabled and approveQuoteV2 refuses the call. Amber → slate,
                because this is a settled fact, not a warning to act on. */}
            {isExpired && isSent && (
                <div className="bg-slate-50 border border-slate-300 rounded-lg p-3 mb-4 flex items-start gap-3">
                    <AlertCircle size={18} className="text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-700">
                        <p className="font-semibold">
                            This {docTerm.word.toLowerCase()} expired{expiredOnText ? ` on ${expiredOnText}` : ''}.
                        </p>
                        <p className="mt-1">
                            It can no longer be approved online. Please contact{' '}
                            {orgInfo.companyName || 'the business'} for an updated price
                            {(orgInfo.phone || orgInfo.email) ? ' — ' : '.'}
                            {orgInfo.phone && <a href={`tel:${orgInfo.phone}`} className="underline font-medium">{orgInfo.phone}</a>}
                            {orgInfo.phone && orgInfo.email && ' or '}
                            {orgInfo.email && <a href={`mailto:${orgInfo.email}`} className="underline font-medium">{orgInfo.email}</a>}
                            {(orgInfo.phone || orgInfo.email) && '.'}
                        </p>
                    </div>
                </div>
            )}

            {/* Approval status banner (post-approval) */}
            {/* r1-dep-portalui-6d2b8f39 — isPostApproval so the signature receipt also
                shows while AwaitingDeposit. Neutral slate styling in that state: the
                green "Approved" treatment would contradict the pay-to-complete card. */}
            {isPostApproval && latestApproval && (
                <div className={`border rounded-lg p-4 mb-4 ${isAwaitingDeposit ? 'bg-slate-50 border-slate-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-semibold ${receipt.strong}`}>
                        {isAwaitingDeposit
                            ? `Signed ${latestApproval.approvedAt?.toDate ? `on ${latestApproval.approvedAt.toDate().toLocaleString()}` : ''}`
                            : `✓ Approved ${latestApproval.approvedAt?.toDate ? `on ${latestApproval.approvedAt.toDate().toLocaleString()}` : ''}`}
                    </p>
                    {/* Deposit receipt — only once the webhook (or an operator) has confirmed it. */}
                    {isApproved && depositPaid && depositAmount > 0 && (
                        <p className="text-sm text-green-800 mt-1 flex items-center gap-1.5">
                            <Check size={14} className="flex-shrink-0" />
                            {/* 1c-portal-receipt-9e4a7d51 — show the received amount (recordedAmount ??
                                amountResolved) and the settled date from paidAt || recordedAt. */}
                            Deposit paid: <span className="font-semibold">{formatCurrency(depositReceiptAmount, currencyCode)}</span>
                            {depositSettledAt?.toDate ? ` on ${depositSettledAt.toDate().toLocaleDateString()}` : ''}
                        </p>
                    )}
                    {latestApproval.approvedBy?.name && (
                        <p className={`text-sm mt-1 ${receipt.body}`}>
                            Signed by: <span className="font-medium">{latestApproval.approvedBy.name}</span>
                        </p>
                    )}
                    {/* r1-p7-portalreceipt-7e4d9b16 — Phase 7.5-portal. Customer-facing
                        receipt: show their electronic signature (same rendering as the
                        operator view + PDF: drawn → inline SVG, typed → navy cursive) and
                        echo back their note. Deliberately EXCLUDES IP / device / duration
                        (latestApproval.metadata) — those are operator-only audit fields. */}
                    {latestApproval.signature?.value && (
                        <div className="mt-3">
                            <p className={`text-xs font-bold uppercase mb-1 ${receipt.label}`}>Your signature</p>
                            <div className={`bg-white border rounded px-3 py-2 inline-block ${receipt.line}`}>
                                {latestApproval.signature.type === 'drawn' ? (
                                    <svg
                                        viewBox="0 0 300 100"
                                        width="220"
                                        height="73"
                                        preserveAspectRatio="xMidYMid meet"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d={latestApproval.signature.value}
                                            fill="none"
                                            stroke="#12192D"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                ) : (
                                    <span style={{
                                        fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                                        fontSize: 26,
                                        color: '#12192D'
                                    }}>
                                        {latestApproval.signature.value}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    {latestApproval.customerNote && (
                        <div className={`mt-3 pt-3 border-t ${receipt.line}`}>
                            <p className={`text-xs font-bold uppercase mb-1 ${receipt.label}`}>Your note:</p>
                            <p className={`text-sm italic whitespace-pre-wrap ${receipt.strong}`}>"{latestApproval.customerNote}"</p>
                        </div>
                    )}
                </div>
            )}
            {isDeclined && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-800">
                    You declined this {docTerm.word.toLowerCase()}. To re-engage, please contact the business directly.
                </div>
            )}
            {isChangesRequested && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-amber-900">Your change request has been sent.</p>
                    <p className="text-sm text-amber-800 mt-1">
                        The business has been notified — they'll be in touch shortly.
                    </p>
                    {quote.customerChangesNote && (
                        <div className="mt-3 pt-3 border-t border-amber-200">
                            <p className="text-xs font-bold text-amber-700 uppercase mb-1">Your note:</p>
                            <p className="text-sm text-amber-900 italic">"{quote.customerChangesNote}"</p>
                        </div>
                    )}
                </div>
            )}

            {/* ───────── LINE ITEMS CARD ───────── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800">{quote.title || `Service ${docTerm.word}`}</h2>{/* r1-p3-docterm-pqdetail-c4f8a1e9 */}
                    {quote.description && (
                        <p className="text-gray-600 whitespace-pre-wrap mt-2 text-sm">{quote.description}</p>
                    )}
                    {/* PROPERTIES v3 — Service location (only when the quote is tied to a property) */}
                    {quote.propertyId && (
                        <div className="mt-3 flex items-start gap-1.5 text-sm text-gray-600">
                            <span className="font-semibold text-gray-500">Service location:</span>
                            <span>{quote.propertyName ? `${quote.propertyName} — ` : ''}{quote.serviceAddress}</span>
                        </div>
                    )}
                </div>

                {/* Legacy-mode banner */}
                {legacyMode && isSent && (
                    <div className="bg-gray-50 border-b border-gray-100 p-3 text-xs text-gray-600 flex items-start gap-2">
                        <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                        <span>This {docTerm.word.toLowerCase()} was created before selective approval was available. All items must be approved together.</span>
                    </div>
                )}

                {/* Line items */}
                <div className="px-6 divide-y divide-gray-100">
                    {orderedItems.map(item => (
                        <LineItemRow
                            key={item.itemId || item.name}
                            item={item}
                            isSelected={selectedItemIds.has(item.itemId)}
                            onToggle={toggleItem}
                            readOnly={!isSent || legacyMode}
                            finalState={
                                /* r1-dep-portalui-6d2b8f39 — isPostApproval: AwaitingDeposit
                                   items are locked to the customer's signed selection too. */
                                isPostApproval && latestApproval?.selections
                                    ? latestApproval.selections.find(s => s.itemId === item.itemId)?.finalState
                                    : undefined
                            }
                            currencyCode={currencyCode}
                        />
                    ))}
                </div>

                {/* Totals row (inline — sticky bar on mobile is separate) */}
                <div className="bg-gray-50 p-6 border-t border-gray-200">
                    <div className="flex flex-col items-end gap-2">
                        <div className="flex justify-between w-full max-w-xs text-gray-600">
                            <span>{isSent && !legacyMode ? 'Selected subtotal' : 'Subtotal'}</span>
                            <span>{formatCurrency(displaySubtotal, currencyCode)}</span>
                        </div>
                        {/* taxlines2-3e0b16a4 — a Settings › Taxes estimate before the server's figures match the ticks: no guessed tax or total */}
                        {isSent && previewPending ? (
                            <div className="flex justify-between w-full max-w-xs text-gray-600">
                                <span>Tax</span>
                                <span>{previewFailed ? '—' : 'Updating…'}</span>
                            </div>
                        ) : displayTaxRows ? displayTaxRows.map((r, i) => (
                            <div key={`tax-${i}`} className="flex justify-between w-full max-w-xs text-gray-600">
                                <span>{taxRowText(r)}</span>
                                <span>{formatCurrency(r.amount, currencyCode)}</span>
                            </div>
                        )) : (
                        <div className="flex justify-between w-full max-w-xs text-gray-600">
                            <span>Tax ({effectiveTaxPct}%)</span>
                            <span>{formatCurrency(displayTax, currencyCode)}</span>
                        </div>
                        )}
                        <div className="flex justify-between w-full max-w-xs text-gray-900 font-bold text-xl mt-2 border-t border-gray-300 pt-3">
                            <span>Total</span>
                            <span>{isSent && previewPending ? (previewFailed ? '—' : 'Updating…') : formatCurrency(displayTotal, currencyCode)}</span>
                        </div>
                        {isSent && previewFailed && (
                            <p role="alert" className="w-full max-w-xs text-sm text-red-600">{PREVIEW_FAILED_TEXT}</p>
                        )}
                        {/* r1-dep-portalui-6d2b8f39 — deposit line. Pre-approval this is a
                            LIVE preview off the current selection (same math the server
                            will run on approve), so it tracks as items are ticked. No
                            surprises at the payment step. Post-approval we show the
                            server's resolved amount instead. */}
                        {isSent && hasDepositPreview && (
                            <div className="flex justify-between w-full max-w-xs text-sm mt-2 pt-2 border-t border-dashed border-gray-300">
                                <span className={depositRequired ? 'text-amber-700 font-semibold' : 'text-gray-600'}>
                                    {depositRequired ? 'Deposit due on approval' : 'Deposit requested'}
                                </span>
                                <span className={depositRequired ? 'text-amber-700 font-semibold' : 'text-gray-600'}>
                                    {formatCurrency(depositPreviewAmount, currencyCode)}
                                </span>
                            </div>
                        )}
                        {isSent && hasDepositPreview && depositRequired && (
                            <p className="text-xs text-amber-700 w-full max-w-xs text-right">
                                Required to complete your approval.
                            </p>
                        )}
                        {isPostApproval && depositUnpaid && (
                            <div className="flex justify-between w-full max-w-xs text-sm mt-2 pt-2 border-t border-dashed border-gray-300 text-gray-600">
                                <span>Deposit due</span>
                                <span>{formatCurrency(depositAmount, currencyCode)}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* r1-1bc-notescard-2f7d9e41 — customer-facing notes & photos. No status
                gate (photos visible before approval by design); hidden entirely when
                there's nothing to show. Photo URLs are signed server-side by
                getCustomerQuoteNotes — the portal can't sign Storage URLs itself. */}
            {notesItems.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
                    <div className="p-6 border-b border-gray-100 flex items-center gap-2">
                        <MessageSquare size={18} className="text-gray-500" />
                        <h2 className="text-lg font-bold text-gray-800">Notes &amp; Photos</h2>
                    </div>
                    <div className="p-6 space-y-4">
                        {notesItems.map((note, i) => {
                            const noteDate = note.addedAtEpoch ? new Date(note.addedAtEpoch) : null;
                            const photos = Array.isArray(note.media)
                                ? note.media.filter(m => m && m.type === 'photo' && m.url)
                                : [];
                            return (
                                <div key={i} className="bg-blue-50 border border-blue-100 rounded-md p-4">
                                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                        {note.authorName && (
                                            <span className="font-semibold text-gray-700">{note.authorName}</span>
                                        )}
                                        {note.authorName && noteDate && <span>·</span>}
                                        {noteDate && <span>{noteDate.toLocaleString()}</span>}
                                    </div>
                                    {note.text && (
                                        <p className="text-gray-700 whitespace-pre-line break-words">{note.text}</p>
                                    )}
                                    {photos.length > 0 && (
                                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {photos.map((m, j) => (
                                                <a
                                                    key={j}
                                                    href={m.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block rounded-md overflow-hidden border border-blue-100 bg-white"
                                                >
                                                    <img
                                                        src={m.thumbUrl || m.url}
                                                        alt=""
                                                        loading="lazy"
                                                        className="w-full h-28 object-cover"
                                                    />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* doced-8f3a61c2 — Stage 5e: the estimate's Terms, in full, after the notes and before the
                Approve buttons (the PDF prints them before the signature). getPortalQuotes sends `terms` only
                when the estimate has them; React escapes the text; line breaks kept. */}
            {typeof quote.terms === 'string' && quote.terms.trim() !== '' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6" aria-label="Terms">
                    <div className="p-6 border-b border-gray-100 flex items-center gap-2">
                        <ScrollText size={18} className="text-gray-500" />
                        <h3 className="text-lg font-bold text-gray-800">Terms</h3>
                    </div>
                    <p className="p-6 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{quote.terms.trim()}</p>
                </div>
            )}

            {/* payhide-7e4c1b05 — actionError used to render ONLY inside the three modals,
                so a failed Pay Deposit or PDF download did nothing visible. Shown here when
                no modal is open (the modals keep showing their own copy, never doubled). */}
            {actionErrorInline && (
                <div ref={actionErrorBannerRef} className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" role="alert">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{actionError}</span>
                </div>
            )}

            {/* ───────── DEPOSIT CARD (r1-dep-portalui-6d2b8f39) ─────────
                Shown post-approval while a deposit is outstanding. `gated` comes from
                the STATUS the server wrote, not from a client recompute: AwaitingDeposit
                means the server held the approval pending payment; Approved + unpaid
                means the deposit is optional. Skipped entirely when there's no deposit,
                which is what keeps the no-deposit flow byte-identical to before. */}
            {isPostApproval && depositUnpaid && (
                <DepositCard
                    gated={isAwaitingDeposit}
                    amount={depositAmount}
                    currencyCode={currencyCode}
                    processing={depositProcessing}
                    timedOut={depositWaitTimedOut}
                    cancelled={depositReturn === 'cancelled'}
                    onPay={handlePayDeposit}
                    paying={payingDeposit}
                    docWord={docTerm.word}
                    stripeReady={orgStripeReady}
                    companyName={orgInfo.companyName}
                />
            )}

            {/* ───────── ACTION BAR (Sent state only) ─────────
                qexp-6f2a9d38-portal — Seam 5: and NOT expired. The whole bar is
                withdrawn rather than shown with three greyed-out buttons: the
                slate banner above already says why, and on mobile this bar is a
                fixed bottom strip that would otherwise sit there eating a third
                of the screen doing nothing.
                approveQuoteV2 §4b refuses an expired approval independently —
                this is the UI half, not the guard. Decline / Request Changes are
                unguarded SERVER-side on purpose; they go with the bar here only
                because the whole bar is one element. */}
            {isSent && !isExpired && (
                <>
                    {/* Desktop: inline action bar */}
                    <div className="hidden md:flex justify-end gap-3 mt-6">
                        <button
                            onClick={() => { setActionError(''); setDeclineModalOpen(true); }}
                            disabled={processing}
                            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                            <X size={16} /> Decline
                        </button>
                        <button
                            onClick={() => { setActionError(''); setRequestChangesModalOpen(true); }}
                            disabled={processing}
                            className="px-5 py-2.5 border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                            <MessageSquare size={16} /> Request Changes
                        </button>
                        {/* SEAM 1d VERIFIER: pqd-approvebtn-desktop-7e2b4d81
                            'Pay Deposit' only when the org can take the payment online.
                            Without Stripe the customer approves and arranges the deposit
                            with the business, so the button describes approving, not paying. */}
                        <button
                            onClick={handleApproveClick}
                            disabled={processing || previewPending}
                            className="px-6 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 font-bold shadow-sm transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                            <Check size={18} />
                            {orgStripeReady && hasDepositPreview && depositRequired
                                ? `Approve & Pay Deposit (${formatCurrency(depositPreviewAmount, currencyCode)})`
                                : (previewPending ? (previewFailed ? 'Approve Selected' : 'Updating total…') : `Approve Selected (${formatCurrency(liveCalc.approvedTotal, currencyCode)})`)}
                        </button>
                    </div>

                    {/* Mobile: sticky bottom bar */}
                    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-3 z-40 safe-area-bottom">
                        <div className="flex justify-between items-center mb-2 px-1">
                            <span className="text-sm font-medium text-gray-600">Approving:</span>
                            <span className="text-lg font-bold text-green-700">{liveCalc.approvedTotal === null ? (previewFailed ? '—' : 'Updating…') : formatCurrency(liveCalc.approvedTotal, currencyCode)}</span>
                        </div>
                        {previewFailed && <p role="alert" className="text-xs text-red-600 mb-2 px-1">{PREVIEW_FAILED_TEXT}</p>}
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setActionError(''); setDeclineModalOpen(true); }}
                                disabled={processing}
                                className="flex-1 px-3 py-2.5 border border-gray-300 text-gray-700 rounded-md font-medium text-xs flex items-center justify-center gap-1 disabled:opacity-40"
                            >
                                <X size={14} /> Decline
                            </button>
                            <button
                                onClick={() => { setActionError(''); setRequestChangesModalOpen(true); }}
                                disabled={processing}
                                className="flex-1 px-3 py-2.5 border border-blue-300 text-blue-700 rounded-md font-medium text-xs flex items-center justify-center gap-1 disabled:opacity-40"
                            >
                                <MessageSquare size={14} /> Changes
                            </button>
                            <button
                                onClick={handleApproveClick}
                                disabled={processing || previewPending}
                                className="flex-1 px-3 py-2.5 bg-green-600 text-white rounded-md font-bold text-xs flex items-center justify-center gap-1 disabled:opacity-40"
                            >
                                {/* SEAM 1d VERIFIER: pqd-approvebtn-mobile-5c8a1f63 */}
                                <Check size={14} /> {orgStripeReady && hasDepositPreview && depositRequired ? 'Approve & Pay' : 'Approve'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ───────── MODALS ───────── */}
            <SignatureModal
                open={signatureModalOpen}
                onClose={() => { if (!processing && !redirectingToPayment) { setSignatureModalOpen(false); setActionError(''); } }}
                onSubmit={handleApproveSubmit}
                approvedTotal={liveCalc.approvedTotal}
                currencyCode={currencyCode}
                processing={processing || redirectingToPayment}
                error={actionError}
                docWord={docTerm.word}
                /* r1-dep-portalui-6d2b8f39 — live preview amount + resolved required flag */
                depositAmount={hasDepositPreview ? depositPreviewAmount : 0}
                depositRequired={depositRequired}
                /* SEAM 1d pqd-sigmodal-callsite-9d4e1f27 — drive the deposit note's copy off readiness */
                stripeReady={orgStripeReady}
                companyName={orgInfo.companyName}
            />
            <DeclineConfirmModal
                open={declineModalOpen}
                onClose={() => { if (!processing) { setDeclineModalOpen(false); setActionError(''); } }}
                onConfirm={handleDeclineConfirm}
                processing={processing}
                error={actionError}
                docWord={docTerm.word}
            />
            <RequestChangesModal
                open={requestChangesModalOpen}
                onClose={() => { if (!processing) { setRequestChangesModalOpen(false); setActionError(''); } }}
                onSubmit={handleRequestChangesSubmit}
                processing={processing}
                error={actionError}
            />
        </div>
    );
};

export default PortalQuoteDetail;